import React, { useEffect, useState } from "react";
import logo from "../assets/images/Logo/web_logo.png";
import { EmailInput, PasswordInput } from "../components/form/inputs";
import PrimaryBtn from "../components/button/PrimaryBtn";
import { Link, useNavigate } from "react-router-dom";
import DecorativeSquares from "../components/ui/DecorativeSquares";
import { useDispatch, useSelector } from "react-redux";
import {
  forgotPassword,
  loginUser,
  varifyCaptcha,
} from "../redux/thunks/loginThunk";
import toast from "react-hot-toast";
import { setToken } from "../redux/slices/authSlice";
import RestoreAccount from "../components/Modals/RestoreAccount";
import { getServiceDetails } from "../redux/thunks/serviceRegThunk";
import { getDashCount } from "../redux/thunks/dashboardThunk";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

const Login = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const tokenKeyFromUrl = params.get("token_key"); // new query param
  const tokenFromUrl = params.get("token");
  const adminIdFromUrl = params.get("admin_id");
  const dispatch = useDispatch();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [UserId, setUserId] = useState("");
  const [UserToken, setUserToken] = useState("");
  const { isLoading } = useSelector((state) => state.login);

  useEffect(() => {
    if (!tokenFromUrl || !tokenKeyFromUrl) return;

    console.log("Login from admin ");

    sessionStorage.setItem("9zIBz91iaMa33UPXalZiOH7mnILWM1af", tokenFromUrl);
    if (adminIdFromUrl) {
      sessionStorage.setItem("is_admin_login", "1");
      sessionStorage.setItem("admin_id", adminIdFromUrl);

      // ✅ custom event trigger
      window.dispatchEvent(new Event("adminLoginChanged"));
    }

    if (!localStorage.getItem("login_toast_shown")) {
      toast.success("Login Successfully", {
        duration: 2000,
        position: "top-right",
      });
      localStorage.setItem("login_toast_shown", "1");
    }

    dispatch(setToken({ userAccessToken: tokenFromUrl, user_id: null }));

    setTimeout(() => {
      navigate("/dashboard");
    }, 100);
  }, [tokenFromUrl, tokenKeyFromUrl, adminIdFromUrl]);

  const validateEmail = () => {
    if (!email) {
      setEmailError("Email is required");
      return false;
    } else if (!/^\S+@\S+\.\S+$/.test(email)) {
      setEmailError("Enter a valid email");
      return false;
    }
    setEmailError("");
    return true;
  };

  const validatePassword = () => {
    if (!password) {
      setPasswordError("Password is required");
      return false;
    }
    setPasswordError("");
    return true;
  };

  const handleLogin = async () => {
    if (!validateEmail() || !validatePassword()) return;

    if (!executeRecaptcha) {
      console.error("reCAPTCHA not ready");
      return;
    }

    try {
      // 1️⃣ Get token
      const reCaptchaToken = await executeRecaptcha("login");

      const captchaPayload = {
        token: reCaptchaToken,
        action: "login",
      };

      // 2️⃣ VERIFY CAPTCHA (IMPORTANT)
      const captchaRes = await dispatch(varifyCaptcha(captchaPayload)).unwrap();

      // console.log("Captcha verified:", captchaRes);

      if (!captchaRes?.success) {
        toast.error("Captcha verification failed");
        return;
      }

      // 3️⃣ LOGIN ONLY AFTER CAPTCHA SUCCESS
      const payload = { username: email, password };

      const res = await dispatch(loginUser(payload)).unwrap();

      if (res.status) {
        const token = res.data.userAccessToken;
        const user = res.data.user_id;

        setUserToken(token);
        setUserId(user);

        if (res.data?.is_account_restored === 1) {
          setShowRestoreModal(true);
          return;
        }

        localStorage.setItem("9zIBz91iaMa33UPXalZiOH7mnILWM1af", token);
        localStorage.setItem("lpCr0HsSLPU3FGP5AUbrIiMUh9lcU6ZS", user);

        dispatch(
          setToken({
            userAccessToken: token,
            user_id: user,
          }),
        );

        toast.success(res.message);

        const serviceRes = await dispatch(getDashCount()).unwrap();
        const isCompleted = serviceRes?.data?.is_registration_completed;

        localStorage.setItem("is_reg_completed", isCompleted);

        navigate("/dashboard");
      } else {
        toast.error(res.message || "Login failed");
      }
    } catch (err) {
      toast.error(
        typeof err === "string" ? err : err?.message || "Something went wrong",
      );
    }
  };

  return (
    <section className="min-h-screen relative overflow-hidden flex items-center justify-center px-4 sm:px-6 lg:px-20 bg-gray-50 lg:bg-gradient-to-br lg:from-orange-50 lg:to-orange-100">
      <DecorativeSquares />

      {/* Card */}
      <div className="w-full max-w-md bg-transparent md:bg-white rounded-2xl shadow-none md:shadow-lg px-6 py-8 sm:px-8 sm:py-10 space-y-6">
        {/* Logo & Tagline */}
        <div className="flex flex-col items-center gap-2">
          <img src={logo} alt="web_logo" className="w-20 h-20 object-contain" />
          <p className="text-gray-400 text-sm text-center">
            You Cook, We Manage the Rest
          </p>
        </div>

        {/* Heading */}
        <h2 className="text-2xl font-semibold text-center text-gray-800">
          Login to your account
        </h2>

        {/* Form */}
        <form
          onSubmit={(e) => {
            e.preventDefault(); // page reload rokne ke liye
            handleLogin();
          }}
          className="space-y-4"
        >
          <EmailInput
            label="Email Address"
            id="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) validateEmail();
            }}
            placeholder="Enter your email"
            required
            error={emailError}
          />

          <PasswordInput
            label="Password"
            id="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (passwordError) validatePassword();
            }}
            placeholder="Enter your password"
            required
            showPasswordToggle
            error={passwordError}
          />

          <div className="text-right">
            <Link
              to={"/forgot-password "}
              className="text-sm text-[#FF6943] cursor-pointer hover:underline"
            >
              Forgot Password?
            </Link>
          </div>

          <div className="flex justify-center">
            <PrimaryBtn
              btnName="Login"
              isLoading={isLoading}
              btnType="submit" // 🔥 important
            />
          </div>

          <p className="text-center text-sm text-gray-500">
            Don’t have an account?{" "}
            <Link
              to={"/register"}
              className="text-[#FF6943] cursor-pointer hover:underline"
            >
              Register
            </Link>
          </p>
        </form>
      </div>
      {showRestoreModal && (
        <>
          <RestoreAccount
            open={showRestoreModal}
            onClose={() => setShowRestoreModal(false)}
            onConfirm={() => {
              const token = UserToken;
              const user = UserId;

              localStorage.setItem("9zIBz91iaMa33UPXalZiOH7mnILWM1af", token);
              localStorage.setItem("lpCr0HsSLPU3FGP5AUbrIiMUh9lcU6ZS", user);

              dispatch(
                setToken({
                  userAccessToken: token,
                  user_id: user,
                }),
              );

              navigate("/dashboard");
            }}
          />
        </>
      )}
    </section>
  );
};

export default Login;
